import PracticeQrService, {
  getPracticeQrOwnerStaffId,
  getPracticeQrToPracticeId,
  hasPracticeQrToPractice,
  type IPracticeQrFilter,
  type TPracticeQr,
  type TPracticeQrCreatePayload,
  type TPracticeQrOwnerStaffRef,
  type TPracticeQrTreatmentType,
} from "@/api/models/qr/practiceQr";
import type { TItemQueryComposableParams } from "@/composables/shared/filters/useGeneralFilter";
import { useGenericMutation } from "@/composables/shared/mutations/useGenericMutation";
import {
  createQueryKeys,
  useGenericPaginatedQuery,
  useGenericQuery,
  type TItemQueryBehaviorOptions,
  type TOwnedFilterParams,
} from "@/composables/shared/queries";
import type { THeader } from "@/composables/shared/useTables";
import type { MaybeRefOrGetter } from "vue";

const PRACTICE_QR_EMPTY_CELL = "—";

export const PRACTICE_QR_WORKFLOW_TREATMENT_TYPES = [
  "select_treatment",
  "referral",
  "referral_internal",
] as const satisfies readonly TPracticeQrTreatmentType[];

export type TPracticeQrWorkflowTreatmentType =
  (typeof PRACTICE_QR_WORKFLOW_TREATMENT_TYPES)[number];

const PRACTICE_QR_WORKFLOW_I18N_KEYS: Record<TPracticeQrWorkflowTreatmentType, string> = {
  select_treatment: "generic.fields.treatmentTypes.select_treatment",
  referral: "models.practice.qr.workflow.directReferral",
  referral_internal: "models.practice.qr.workflow.directReferralInternal",
};

const PRACTICE_QR_WORKFLOW_ICONS: Record<
  Exclude<TPracticeQrWorkflowTreatmentType, "referral">,
  string
> = {
  select_treatment: "mdi-format-list-bulleted",
  referral_internal: "mdi-arrow-right-bold",
};

export type TPracticeQrCardType = "internal" | "partner" | "partnerSelectPlan";
export type TPracticeQrPartnerDirection = "to_my_office" | "from_my_office";

const PRACTICE_QR_REFERRAL_WORKFLOW_ICONS: Record<TPracticeQrPartnerDirection, string> = {
  from_my_office: "mdi-arrow-top-right-thick",
  to_my_office: "mdi-arrow-bottom-left-thick",
};

const PRACTICE_QR_CARD_ICON_COLORS: Record<TPracticeQrCardType, string> = {
  internal: "info",
  partner: "warning",
  partnerSelectPlan: "success",
};

export const isPracticeQrWorkflowTreatmentType = (
  treatmentType: TPracticeQrTreatmentType
): treatmentType is TPracticeQrWorkflowTreatmentType =>
  (PRACTICE_QR_WORKFLOW_TREATMENT_TYPES as readonly string[]).includes(treatmentType);

export const getPracticeQrWorkflowLabelKey = (treatmentType: TPracticeQrTreatmentType): string =>
  isPracticeQrWorkflowTreatmentType(treatmentType)
    ? PRACTICE_QR_WORKFLOW_I18N_KEYS[treatmentType]
    : `generic.fields.treatmentTypes.${treatmentType}`;

const PRACTICE_QR_PARTNER_REFERRAL_TREATMENT_TYPE =
  "referral" as const satisfies TPracticeQrTreatmentType;

const PRACTICE_QR_PARTNER_SELECT_PLAN_TREATMENT_TYPE =
  "select_treatment" as const satisfies TPracticeQrTreatmentType;

const PRACTICE_QR_PARTNER_TREATMENT_TYPES = [
  PRACTICE_QR_PARTNER_REFERRAL_TREATMENT_TYPE,
] as const satisfies readonly TPracticeQrTreatmentType[];

export const PRACTICE_QR_INTERNAL_TREATMENT_TYPES = [
  "select_treatment",
  "referral_internal",
] as const satisfies readonly TPracticeQrTreatmentType[];

/** Partner collaboration QR (B2B direct referral to a cooperating office). */
export const isPartnerReferralPracticeQr = (item: TPracticeQr): boolean =>
  item.treatment_type === PRACTICE_QR_PARTNER_REFERRAL_TREATMENT_TYPE;

/** Treatment collaboration QR (select plan with a cooperating office). */
export const isPartnerSelectPlanPracticeQr = (item: TPracticeQr): boolean =>
  item.treatment_type === PRACTICE_QR_PARTNER_SELECT_PLAN_TREATMENT_TYPE &&
  hasPracticeQrToPractice(item);

/** Partner collaboration QR (referral or treatment collaboration with a cooperating office). */
export const isPartnerPracticeQr = (item: TPracticeQr): boolean =>
  isPartnerReferralPracticeQr(item) || isPartnerSelectPlanPracticeQr(item);

/** Internal QR (select plan or internal referral without a cooperating office). */
export const isInternalPracticeQr = (item: TPracticeQr): boolean => {
  if (item.treatment_type === "referral_internal") return true;
  return item.treatment_type === "select_treatment" && !hasPracticeQrToPractice(item);
};

export const getPracticeQrCardType = (item: TPracticeQr): TPracticeQrCardType => {
  if (isPartnerReferralPracticeQr(item)) return "partner";
  if (isPartnerSelectPlanPracticeQr(item)) return "partnerSelectPlan";
  return "internal";
};

export const hasPracticeQrOwnerStaff = (item: TPracticeQr): boolean =>
  Boolean(getPracticeQrOwnerStaffId(item.owner_staff)?.trim());

export const getPracticeQrCardIcon = (cardType: TPracticeQrCardType): string => {
  if (cardType === "internal") return "mdi-account-group-outline";
  if (cardType === "partnerSelectPlan") return "mdi-human-greeting-proximity";
  return "mdi-handshake-outline";
};

export const getPracticeQrCardLabelKey = (cardType: TPracticeQrCardType): string => {
  if (cardType === "partner") return "models.practice.qr.cards.partner.title";
  if (cardType === "partnerSelectPlan") return "models.practice.qr.cards.partnerSelectPlan.title";
  return "models.practice.qr.cards.internal.title";
};

export const getPracticeQrIconColor = (item: TPracticeQr): string =>
  PRACTICE_QR_CARD_ICON_COLORS[getPracticeQrCardType(item)];

/** Partner QR direction relative to the viewing practice (matches generate-dialog payload mapping). */
export const getPartnerPracticeQrDirection = (
  item: TPracticeQr,
  myPracticeId: string
): TPracticeQrPartnerDirection | null => {
  if (!isPartnerPracticeQr(item)) return null;

  const myId = myPracticeId.trim();
  const practiceId = item.practice?.trim() ?? "";
  const toPracticeId = getPracticeQrToPracticeId(item.treatment_data?.to_practice);

  if (practiceId === myId) return "from_my_office";
  if (toPracticeId === myId) return "to_my_office";

  return null;
};

/** Cooperating office id for partner QR, relative to the viewing practice. */
export const getPartnerCooperatingPracticeId = (
  item: TPracticeQr,
  myPracticeId: string
): string => {
  const practiceId = item.practice?.trim() ?? "";
  const toPracticeId = getPracticeQrToPracticeId(item.treatment_data?.to_practice);
  const myId = myPracticeId.trim();

  if (practiceId && practiceId !== myId) return practiceId;
  if (toPracticeId && toPracticeId !== myId) return toPracticeId;

  return practiceId || toPracticeId;
};

export const getPracticeQrWorkflowIcon = (item: TPracticeQr, myPracticeId: string): string => {
  if (item.treatment_type === "referral") {
    const direction = getPartnerPracticeQrDirection(item, myPracticeId);
    return direction
      ? PRACTICE_QR_REFERRAL_WORKFLOW_ICONS[direction]
      : PRACTICE_QR_REFERRAL_WORKFLOW_ICONS.to_my_office;
  }

  return isPracticeQrWorkflowTreatmentType(item.treatment_type)
    ? PRACTICE_QR_WORKFLOW_ICONS[item.treatment_type]
    : "mdi-format-list-bulleted";
};

export type TPracticeQrFlowFilter = "all" | "internal" | "partner";

export const PRACTICE_QR_FLOW_FILTER_VALUES = [
  "all",
  "internal",
  "partner",
] as const satisfies readonly TPracticeQrFlowFilter[];

export const getPracticeQrFlowFilterLabelKey = (filter: TPracticeQrFlowFilter): string => {
  if (filter === "all") return "models.notification.filters.all";
  return getPracticeQrCardLabelKey(filter);
};

export type TPracticeQrIconLabel = { label: string; icon: string; iconColor: string };

export const getPracticeQrCardDisplay = (
  item: TPracticeQr,
  t: (key: string) => string
): TPracticeQrIconLabel => {
  const cardType = getPracticeQrCardType(item);
  const labelKey = getPracticeQrCardLabelKey(cardType);
  const translated = t(labelKey);

  return {
    label: translated !== labelKey ? translated : cardType,
    icon: getPracticeQrCardIcon(cardType),
    iconColor: getPracticeQrIconColor(item),
  };
};

export const getPracticeQrWorkflowDisplay = (
  item: TPracticeQr,
  myPracticeId: string,
  t: (key: string) => string
): TPracticeQrIconLabel => {
  const labelKey = getPracticeQrWorkflowLabelKey(item.treatment_type);
  const translated = t(labelKey);

  return {
    label: translated !== labelKey ? translated : item.treatment_type,
    icon: getPracticeQrWorkflowIcon(item, myPracticeId),
    iconColor: getPracticeQrIconColor(item),
  };
};

export const getPracticeQrCooperatingOfficeLabel = (
  item: TPracticeQr,
  myPracticeId: string,
  resolvePracticeName: (practiceId: string) => string | undefined,
  t: (key: string) => string
): string => {
  if (!isPartnerPracticeQr(item)) return PRACTICE_QR_EMPTY_CELL;

  const cooperatingId = getPartnerCooperatingPracticeId(item, myPracticeId);
  if (!cooperatingId) return PRACTICE_QR_EMPTY_CELL;

  const officeName = resolvePracticeName(cooperatingId);
  if (!officeName) return PRACTICE_QR_EMPTY_CELL;

  if (isPartnerSelectPlanPracticeQr(item)) {
    return `${t("models.practice.qr.direction.with")} ${officeName}`;
  }

  const direction = getPartnerPracticeQrDirection(item, myPracticeId);

  if (!direction) return officeName;

  const prefix = direction === "from_my_office" ? t("generic.fields.to") : t("generic.fields.from");

  return `${prefix} ${officeName}`;
};

export const getPracticeQrOwnerStaffLabel = (
  ref: TPracticeQrOwnerStaffRef | undefined,
  staffById: Map<string, { first_name?: string; last_name?: string }>,
  formatStaffName: (staff: { first_name?: string; last_name?: string }) => string
): string => {
  if (ref == null) return PRACTICE_QR_EMPTY_CELL;

  if (typeof ref !== "string") {
    const composed = [ref.first_name, ref.last_name].filter(Boolean).join(" ").trim();
    if (composed) return composed;
  }

  const staff = staffById.get(getPracticeQrOwnerStaffId(ref));
  return staff ? formatStaffName(staff) : PRACTICE_QR_EMPTY_CELL;
};

export const getPracticeQrEventLabel = (item: TPracticeQr): string => {
  const label = [item.event?.campaignSource, item.event?.campaignName].filter(Boolean).join(" / ");

  return label || PRACTICE_QR_EMPTY_CELL;
};

export const PRACTICE_QR_TABLE_HEADERS: THeader[] = [
  {
    key: "qr_type",
    translationKey: "models.practice.qr.flowType",
    sortable: false,
    width: "18%",
  },
  {
    key: "workflow",
    translationKey: "models.practice.qr.workflow.label",
    sortable: false,
    width: "12%",
  },
  {
    key: "cooperating_practice",
    translationKey: "models.practice.qr.cooperatingOffice",
    sortable: false,
    width: "20%",
  },
  {
    key: "owner_staff",
    translationKey: "models.practice.qr.table.headers.owner",
    sortable: false,
    width: "16%",
  },
  {
    key: "event",
    translationKey: "models.practice.qr.table.headers.event",
    sortable: false,
    width: "12%",
  },
  {
    key: "usages_count",
    translationKey: "models.practice.qr.table.headers.registerPatientCount",
    sortable: false,
    align: "end",
    width: "16%",
  },
];

export const PRACTICE_QR_TABLE_INITIAL_KEYS = PRACTICE_QR_TABLE_HEADERS.map(({ key }) => key);

export const buildPracticeQrPaginatedFilters = (
  practiceId: string,
  flowFilter: TPracticeQrFlowFilter
): Partial<IPracticeQrFilter> => {
  const filters: Partial<IPracticeQrFilter> = {
    participatory_practice: practiceId,
  };

  if (flowFilter === "internal") {
    filters.treatment_type__in = [...PRACTICE_QR_INTERNAL_TREATMENT_TYPES];
  } else if (flowFilter === "partner") {
    filters.treatment_type__in = [...PRACTICE_QR_PARTNER_TREATMENT_TYPES];
  }

  return filters;
};

const PracticeQrQueryKeys = createQueryKeys(PracticeQrService.queryKey, (key) => ({
  paginated: key<IPracticeQrFilter>("paginated"),
  detail: key<string>("detail"),
}));

export const usePracticeQrPaginated = (params: TOwnedFilterParams<IPracticeQrFilter> = {}) => {
  return useGenericPaginatedQuery<TPracticeQr, Partial<IPracticeQrFilter>>({
    params,
    queryKey: PracticeQrQueryKeys.paginated(),
    fetcher: async (fetchParams) => {
      return await PracticeQrService.getPaginatedFiltered(
        {},
        fetchParams.filters,
        fetchParams.pageParam
      );
    },
    permission: $permSubjects.ORBISQR__PRACTICEQRCODE,
  });
};

export const useCreatePracticeQr = () => {
  return useGenericMutation<TPracticeQr, TPracticeQrCreatePayload>({
    mutationFn: (payload) => PracticeQrService.create({}, payload),
    invalidates: [PracticeQrQueryKeys.paginated()],
    feature: "practice-qr-create",
    params: {
      permissions: { create: $permSubjects.ORBISQR__PRACTICEQRCODE },
    },
  });
};

export const usePracticeQrDetailRead = (
  params: MaybeRefOrGetter<TItemQueryComposableParams<string>>,
  options: TItemQueryBehaviorOptions = {}
) => {
  return useGenericQuery<TPracticeQr>({
    queryKey: PracticeQrQueryKeys.detail(),
    fetcher: (id) => PracticeQrService.get({ id }),
    permission: $permSubjects.ORBISQR__PRACTICEQRCODE,
    params,
    ...options,
  });
};
